import { HospitalModel, StoreCommunicationStatModel } from "@common/models";
import { HospitalService, HospitalSalesItemFavoritesService  } from "@common/services";
import { AppStore, ClientSearchStore, NotificationStoreInstance } from "@common/stores";
import { action, autorun, computed, flow, makeObservable, observable, runInAction } from "mobx";
import { ClinicTaskSearchStore } from "../ClinicTaskSearchStore";
import { HospitalHubStore } from "../Hubs/HospitalHub";
import { AppointmentSubscriberStore } from "../Hubs/AppointmentSubscriberStore";
import { DateTime } from "luxon";
import { IDisposable } from "@common/types";
import { StoreService } from "@common/services/storeService";
import { ClientSMSHubSubscriberStore } from "@common/stores/subscribers/ClientSMSHubSubscriberStore";
import { CommunicationEventType } from "@common/models/Enums";
import { ClientDocumentRequestModel } from "@common/models/ClientDocumentRequestModel";
import NotificationToastLinkBody from '@common/components/utility/NotificationToastLinkBody';
import React from 'react';

export type HospitalAppStoreInit = {
	appStore: AppStore;
	hospitalHub: HospitalHubStore;
};
export class HospitalAppStore implements IDisposable {
	appStore: AppStore;
	clientSearchStore: ClientSearchStore = new ClientSearchStore();
	@observable currentHospital?: HospitalModel;
	@observable isHospitalLoading: boolean = false;
	@observable adminNavActiveKey: string | undefined = undefined;
	@observable connectedHospitalId: number | undefined = undefined;
	hospitalHub: HospitalHubStore;
	@observable private _appointmentSubscriber: AppointmentSubscriberStore;
	@observable private _calendarLoadedDate: DateTime;
	@observable unreadMessageCount: number | undefined = undefined;
	@observable unreadMessageClientCount: number | undefined = undefined;
	@observable currentCommunicationType: CommunicationEventType | undefined;
	@observable private _clientSMSSubscriber: ClientSMSHubSubscriberStore;
	constructor(init: HospitalAppStoreInit) {
		this.appStore = init.appStore;
		this.hospitalHub = init.hospitalHub;
		this._calendarLoadedDate = DateTime.utc();
		this._appointmentSubscriber = new AppointmentSubscriberStore(this);
		this._clientSMSSubscriber = new ClientSMSHubSubscriberStore();
		makeObservable(this, undefined);
		this.initMessageHub();
	}

	@action.bound setCurrentHospital(hospital: HospitalModel) {
		this.currentHospital = hospital;
	}

	dispose() {
		this._appointmentSubscriber.dispose;
		this._clientSMSSubscriber.dispose;
	}

	@computed get appointmentSubscriber() {
		return this._appointmentSubscriber;
	}

	@computed get clientSMSSubscriber() {
		return this._clientSMSSubscriber;
	}

	@computed get calendarLoadedDate() {
		return this._calendarLoadedDate;
	}

	@action.bound setCalendarLoadedDate(date: DateTime) {
		this._calendarLoadedDate = date;
	}

	@action.bound private initMessageHub() {
		//registerMethod is a helper on the raw hubs "on" methood, this subscribes a client to a client message on the Hub
		this.hospitalHub.registerMethod({ eventName: "onHospitalTaskUpdated", callback: this.OnTaskUpdatedMessage });
		this.hospitalHub.registerMethod({ eventName: "onUnreadHospitalMessagesUpdated", callback: this.OnUnReadHospitalMessagesUpdated});
		this.hospitalHub.registerMethod({ eventName: "onDocumentGenerationStatusUpdated", callback: this.OnDocumentGenerationStatusUpdated});

		//using an auto run in the store to run anytime the current hospital changes, we do this to handle login and logout
		//to remove the current user or add the current user to the signalr group for the hospital.
		autorun(async () => {
			const hospitalId = this.currentHospital?.hospitalId;
			if (hospitalId)
				this.InitUnReadMessagesCount(hospitalId);
			if (this.connectedHospitalId && (!hospitalId || this.connectedHospitalId !== hospitalId)) {
				await this.hospitalHub.leaveHospitalGroup(this.connectedHospitalId);
				runInAction(() => (this.connectedHospitalId = undefined));
			}
			if (hospitalId && hospitalId !== this.connectedHospitalId) {
				await this.hospitalHub.joinHospitalGroup(hospitalId);
				runInAction(() => (this.connectedHospitalId = hospitalId));
			}
		});
	}

	//#region HospitalHub Events
	@action.bound OnUnReadHospitalMessagesUpdated = (storeId: number, unreadMessageCount: number, unreadMessageClientCount: number, communicationEventType: CommunicationEventType) => {
		if(storeId === this.currentHospital?.hospitalId) {
			this.unreadMessageCount = unreadMessageCount;
			this.unreadMessageClientCount = unreadMessageClientCount;
			this.currentCommunicationType = communicationEventType;
		}
	}

	@action.bound InitUnReadMessagesCount = (storeId: number) => {
		if(_.isUndefined(this.unreadMessageCount)) {
			StoreService.getStoreCommunicationStats(storeId)
			.then((data:StoreCommunicationStatModel) => runInAction(() => {
				this.unreadMessageCount = data.unreadMessageCount;
				this.unreadMessageClientCount = data.unreadMessageClientCount;
			}))
			.catch(() => { }); //Intentional empty catch left to allow tests to pass
		}
	}

	@action.bound OnTaskUpdatedMessage = (task: number, hospitalId: number, userIds: number[], roleName: string) => {
		//a new task was created from the back end, check if it's for the current hospital (it always should be because it should only send updates to the hospital group for the task)
		if (this.currentHospital && this.currentHospital.hospitalId === hospitalId) {
			const hasUsers = userIds.length > 0;
			const hasRole = !!roleName;
			//ensure the task either targets the current user or one of the current users roles
			let shouldUpdate = !hasUsers || _.some(userIds, a => a === this.appStore.configuration.userId);
			shouldUpdate = shouldUpdate && (!hasRole || this.appStore.configuration.userHasRole(roleName));
			if (shouldUpdate) {
				//the task is for the current user or their roles, reexecute the task search
				if (this.openTaskSearch) {
					this.openTaskSearch.updateSearchFilter({ currentUserId: this.appStore.configuration.userId, hospitalId: this.currentHospital.hospitalId });
					this.openTaskSearch.search();
				}
			}
		}
	};

	@action.bound OnDocumentGenerationStatusUpdated = (clientDocumentRequestModel: ClientDocumentRequestModel) => {
		const isForuser = clientDocumentRequestModel.userId === this.appStore.configuration.userId;
		if(isForuser)
		{
			switch (clientDocumentRequestModel.documentRequestStatusId)
			{
				case 3:
					{
						if(clientDocumentRequestModel.storageContent)
						{
							const myComponentInstance = React.createElement(NotificationToastLinkBody, { linkText: 'View PDF', linkUrl: `${clientDocumentRequestModel.storageContent?.storageContentUrl}` });
							const wasEmailed = _.isEmpty(clientDocumentRequestModel.emailAddress) ? '' : ` The document was emailed to ${clientDocumentRequestModel.emailAddress}.`;
							
							NotificationStoreInstance.success(`Your document generation request for ${clientDocumentRequestModel.clientName} has completed.${wasEmailed}`, true, myComponentInstance);
						} else
						{
							NotificationStoreInstance.info(`Your document generation request for ${clientDocumentRequestModel.clientName} has completed, but contained no documents.`, true);
						}
						break;
					}
				case 5:
					{
						NotificationStoreInstance.error(`Your document generation request for ${clientDocumentRequestModel.clientName} has failed.`, true);
						break;						
					}
				default:
					break;
			}
			
			//console.log(`document generation request for ${clientDocumentRequestModel.clientName} status changed to ${clientDocumentRequestModel.documentRequestStatusName}.`)

		}
	}

	//#endregion

	@computed get openTaskSearch() {
		if (this.hasCurrentHospital) {
			const openTasks = new ClinicTaskSearchStore({ hospitalId: Number(this.currentHospital!.hospitalId), openTasks: true, currentUserId: this.appStore.configuration.userId, top: 0, explicitLoad: false });
			openTasks.search();
			return openTasks;
		}
		return undefined;
	}

	@computed get hasCurrentHospital(): boolean {
		return this.currentHospital ? true : false;
	}

	@computed get hospitalTimezone(): string {
		return this.currentHospital?.store?.timezoneName ?? "utc";
	}

	@action.bound navAccordionToggled = (key?: string) => {
		// the key represents the accordion section that's being toggled.
		// we can infer the toggle state by comparing the existing active key to the new one.
		this.adminNavActiveKey = this.adminNavActiveKey === key ? undefined : key;
	};

	@action.bound tryUpdateCurrentHospital(hospital?: HospitalModel) {
		if (this.currentHospital && this.currentHospital.hospitalId === hospital?.hospitalId) this.currentHospital = hospital;
	}
	
	loginHospital = flow(function* (this: HospitalAppStore, hospitalId: number) {
		this.clearHospitalCommsStats();
		if (!this.hasCurrentHospital || this.currentHospital?.hospitalId !== hospitalId) {
			this.isHospitalLoading = true;
			this.currentHospital = undefined;
			this.currentHospital = yield HospitalService.getHospitalForUser(hospitalId, { promiseArea: "loggedInHospital" }).catch(NotificationStoreInstance.onError("Unable to load Hospital", undefined));
			this.isHospitalLoading = false;
		}
	});

	@action.bound logoutHospital = () => {
		this.currentHospital = undefined;
		this.clearHospitalCommsStats();
	};

	@action.bound toggleHospitalSalesItemFavorite = flow(function* (this: HospitalAppStore,	salesItemOptionId: number) {
		if (!this.currentHospital) return;
		const hospital = this.currentHospital;
		const existing = hospital.hospitalSalesItemFavorites?.find(f => f.salesItemOptionId === salesItemOptionId);
		if (existing) {
			yield HospitalSalesItemFavoritesService.deleteHospitalSalesItemFavorite(
			hospital.hospitalId,
			salesItemOptionId
			);

			runInAction(() => {
			hospital.hospitalSalesItemFavorites =
				hospital.hospitalSalesItemFavorites.filter(
				f => f.salesItemOptionId !== salesItemOptionId
				);
			});
		} else {
			const result =
			yield HospitalSalesItemFavoritesService.addHospitalSalesItemFavorite({
				hospitalSalesItemFavoriteId: 0,
				hospitalId: hospital.hospitalId,
				salesItemOptionId,
				rowVersion: "",
			});

			runInAction(() => {
			hospital.hospitalSalesItemFavorites = [
				...hospital.hospitalSalesItemFavorites,
				result
			];
			});
		}
	});

	@action.bound clearHospitalCommsStats = () => {
		this.unreadMessageClientCount = undefined;
		this.unreadMessageCount = undefined;
	};
}
